// accountTree.js — pure helpers for the multi-level account categories
// (bo-api 2026-10-26: bo_account_item.parent_id / platform_id).
//
// The API already returns items in tree order with `path_name`, `depth` and the
// effective platform. These helpers exist so the pages can (a) build dropdown
// options and parent pickers without re-implementing the rules inline in a
// .vue file jest cannot mount, and (b) still behave when an item lacks the
// server fields (e.g. an older API, or a freshly created row).
//
// Rules mirrored from controllers/BO-AccountTree.go:
//   - parent_id null/0/missing parent = top level
//   - siblings sort by lower-cased name in code-point order, then id
//   - every walk is cycle-safe
//   - a category's effective platform = its own platform_id, else the nearest
//     ancestor's

export const PATH_SEPARATOR = " > ";

function pid(item) {
	const p = item && item.parent_id;
	return p === null || p === undefined || p === "" ? 0 : Number(p) || 0;
}

function cmpName(a, b) {
	const an = String(a.name || "").toLowerCase();
	const bn = String(b.name || "").toLowerCase();
	if (an < bn) return -1;
	if (an > bn) return 1;
	return (a.id || 0) - (b.id || 0);
}

/**
 * indexTree builds { byId, children } — children keyed by parent id (0 = roots).
 * A row whose parent is not in `items` (deleted / filtered out) is a root.
 */
export function indexTree(items) {
	const byId = {};
	(items || []).forEach((it) => {
		byId[it.id] = it;
	});
	const children = {};
	(items || []).forEach((it) => {
		let p = pid(it);
		if (p === it.id || !byId[p]) p = 0;
		(children[p] = children[p] || []).push(it);
	});
	Object.keys(children).forEach((k) => children[k].sort(cmpName));
	return { byId, children };
}

function parentOf(idx, it) {
	const p = pid(it);
	return p !== it.id && idx.byId[p] ? p : 0;
}

/** pathItems returns the chain root → … → id (cycle-safe; [] when unknown). */
export function pathItems(items, id, idx) {
	const t = idx || indexTree(items);
	const out = [];
	const seen = {};
	let cur = id;
	while (cur && !seen[cur] && t.byId[cur]) {
		seen[cur] = true;
		out.unshift(t.byId[cur]);
		cur = parentOf(t, t.byId[cur]);
	}
	return out;
}

/** pathName joins the names along the path: "A > B > C". */
export function pathName(items, id, idx) {
	return pathItems(items, id, idx)
		.map((it) => it.name)
		.join(PATH_SEPARATOR);
}

/**
 * treeRows returns every item in tree order (parents first, siblings by name),
 * each copied with `_depth` and `_path` (computed client-side). Items stuck in
 * a hand-made cycle are appended at the end so nothing disappears.
 */
export function treeRows(items) {
	const idx = indexTree(items);
	const out = [];
	const seen = {};
	const walk = (p, depth) => {
		(idx.children[p] || []).forEach((it) => {
			if (seen[it.id]) return;
			seen[it.id] = true;
			out.push({ ...it, _depth: depth, _path: pathName(items, it.id, idx) });
			walk(it.id, depth + 1);
		});
	};
	walk(0, 0);
	(items || [])
		.filter((it) => !seen[it.id])
		.sort(cmpName)
		.forEach((it) => out.push({ ...it, _depth: 0, _path: pathName(items, it.id, idx) }));
	return out;
}

/** descendantIds — every id strictly below id (cycle-safe). */
export function descendantIds(items, id) {
	const idx = indexTree(items);
	const out = {};
	const stack = (idx.children[id] || []).map((c) => c.id);
	while (stack.length) {
		const n = stack.pop();
		if (n === id || out[n]) continue;
		out[n] = true;
		(idx.children[n] || []).forEach((c) => stack.push(c.id));
	}
	return Object.keys(out).map(Number);
}

/**
 * orderedRows — the API already returns categories in tree order with
 * path_name/depth computed over ALL live categories. When every row carries
 * them, keep that order: re-walking a FILTERED list (e.g. is_active=true) would
 * turn a child of an inactive parent into a root and move it out of place.
 * Rows without server fields fall back to the client-side walk.
 */
export function orderedRows(items) {
	const rows = items || [];
	if (rows.length && rows.every((r) => r.path_name !== undefined && r.path_name !== null)) {
		return rows.map((r) => ({ ...r, _depth: r.depth !== undefined ? r.depth : 0, _path: r.path_name }));
	}
	return treeRows(rows);
}

/** label prefers the server's path_name (computed over ALL live categories). */
function labelOf(row) {
	return row.path_name || row._path || row.name;
}

/**
 * categoryOptions — the ONE dropdown of the record form (single + batch): every
 * category of `type` (active only unless includeInactive), in tree order,
 * labelled with the full path so Element UI's built-in filter (case-insensitive
 * substring of the label) finds it by any segment of the path.
 * Returns [{ value, label, depth }].
 */
export function categoryOptions(items, type, { includeInactive = false } = {}) {
	return orderedRows(items)
		.filter((r) => (type ? Number(r.type) === Number(type) : true))
		.filter((r) => includeInactive || r.is_active)
		.map((r) => ({ value: r.id, label: labelOf(r), depth: r.depth !== undefined ? r.depth : r._depth }));
}

/**
 * parentOptions — the "หมวดหมู่แม่" picker of the category dialog: categories of
 * the same type, excluding the category itself and everything below it (either
 * would make a cycle). Inactive categories are allowed as parents.
 */
export function parentOptions(items, { type, selfId } = {}) {
	const banned = {};
	if (selfId) {
		banned[selfId] = true;
		descendantIds(items, selfId).forEach((d) => {
			banned[d] = true;
		});
	}
	return treeRows(items)
		.filter((r) => !banned[r.id])
		.filter((r) => (type ? Number(r.type) === Number(type) : true))
		.map((r) => ({ value: r.id, label: labelOf(r), depth: r._depth }));
}

/**
 * inheritedPlatform — what a category placed under `parentId` would inherit
 * when its own platform is empty: { platformId, fromId, fromPath } or null.
 */
export function inheritedPlatform(items, parentId) {
	if (!parentId) return null;
	const idx = indexTree(items);
	const chain = pathItems(items, parentId, idx);
	for (let i = chain.length - 1; i >= 0; i--) {
		const p = chain[i].platform_id;
		if (p !== null && p !== undefined && Number(p) !== 0) {
			return { platformId: Number(p), fromId: chain[i].id, fromPath: pathName(items, chain[i].id, idx) };
		}
	}
	return null;
}

/** platformName looks a platform id up in the GET /account/platforms list. */
export function platformName(platforms, id) {
	if (id === null || id === undefined) return "";
	const p = (platforms || []).find((x) => Number(x.id) === Number(id));
	return p ? p.name : "";
}

/**
 * itemPayload — the create/update body of the category dialog. parent_id and
 * platform_id are always sent (null = top level / no binding) so an edit that
 * clears them reaches the API; platform is forced to null for income.
 */
export function itemPayload(form) {
	const parent = Number(form.parent_id) || null;
	const platform = Number(form.type) === 2 ? Number(form.platform_id) || null : null;
	return {
		name: form.name,
		type: form.type,
		description: form.description,
		is_active: form.is_active,
		parent_id: parent,
		platform_id: platform,
	};
}

/** matchesQuery mirrors Element UI 2.4's default option filter on our labels. */
export function matchesQuery(label, query) {
	if (!query) return true;
	return String(label || "")
		.toLowerCase()
		.indexOf(String(query).toLowerCase()) !== -1;
}
